import { useEventCallback } from '@mui/material';
import { Fragment, ReactElement, useState } from 'react';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import FilterResearchResponsible from 'practice/components/resonsible/research/filter';
import TableResponsible from 'practice/components/resonsible/research/table-responsible';
import { validationSchema } from 'practice/constants/research-responsible-schema';
import ListHeader from 'shell/components/shared/list-header';
import { useNavigate } from 'react-router-dom';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { PracticeAuthorizations } from 'shell/authorizations/practice';
import { Form, Formik } from 'formik';
import { ResearchResponsibleCrubs } from 'practice/constants/responsabile-crubs';
import { ResponsibleData } from 'shell/models/type-responsible';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { AxiosResponse } from 'axios';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { transformDataset } from './utils';
import {
  BodyResearchResponsible,
  ResearchResponsibleVM,
} from 'practice/view-models/research-responsible';

export const authsObj = Object.freeze({
  read: [PracticeAuthorizations.RicercaResponsabile],
  create: [PracticeAuthorizations.NuovoResponsabile],
  edit: [PracticeAuthorizations.ModificaResponsabile],
  delete: [PracticeAuthorizations.EliminaResponsabile],
});

const ResearchResponsible = (): ReactElement => {
  const navigate = useNavigate();

  const [responsible, getResponsible] = useHttpFetch<
    HttpResponseList<ResponsibleData>,
    Array<BodyResearchResponsible>
  >(`${__PRACTICE_API_URL__}/pratiche/responsabile`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: {},
    },
    transformer: (response: AxiosResponse<HttpResponseList<ResponsibleData>>) => {
      const { data } = response;
      return data?.payload?.list?.map((item) => ({
        practice: item.key?.tipoPratica || '',
        responsible: item.codResponsabile || '',
        service: item.key?.codServizio || '',
      }));
    },
  });

  const onSubmitFilter = useEventCallback((values: ResearchResponsibleVM) => {
    const queryString = transformDataset(values);

    getResponsible({
      params: {},
      paramsSerializer: () => queryString,
    });
  });

  const auths = useActionAuthorization(authsObj);

  const deleteResponsibile = useHttpClient(`${__PRACTICE_API_URL__}/pratiche/responsabile`, {
    options: { method: 'DELETE' },
  });

  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<BodyResearchResponsible>>({
    element: null,
    open: false,
  });

  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<BodyResearchResponsible>>(
    {
      element: null,
      open: false,
    }
  );

  return (
    <Fragment>
      <Breadcrumbs items={ResearchResponsibleCrubs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuovo responsabile"
        addBtnLabel="Aggiungi nuovo responsabile"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/pratiche/ricerca-responsabile/nuovo');
        }}
        title="Ricerca Responsabile"
      />
      <div className="circle" />
      <Formik
        initialValues={{
          filter: { codeResponsible: '', codeService: '', typePractice: '', orders: {} },
        }}
        onSubmit={(values) => onSubmitFilter(values)}
        validationSchema={validationSchema}
      >
        <Form>
          <FilterResearchResponsible />
          {responsible ? (
            <TableResponsible
              activeDelete={auths.delete}
              activeEdit={auths.edit}
              getResponsible={getResponsible}
              responsibles={responsible.data as Array<BodyResearchResponsible>}
              setDeleteAlertModal={setDeleteAlertModal}
            />
          ) : undefined}
        </Form>
      </Formik>
      <DeleteAlertModal
        title="Cancellazione Responsabile"
        message="Confermare la cancellazione?"
        open={deleteAlertModal.open}
        onClose={() => setDeleteAlertModal({ open: false, element: null })}
        onYes={() => {
          (
            deleteResponsibile({
              url: `/${deleteAlertModal.element?.practice}-${deleteAlertModal.element?.service}`,
            }) as ResponsePromise<void>
          ).then(() => {
            setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
            setDeleteAlertModal({ open: false, element: null });
          });
        }}
        onNo={() => setDeleteAlertModal({ open: false, element: null })}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Responsabile"
        message={`La cancellazione della funzione ${deleteSuccessModal.element?.responsible} è avvenuta con successo.`}
        onClose={() => {
          onSubmitFilter({
            filter: { codeResponsible: '', codeService: '', typePractice: '', orders: {} },
          });
          setDeleteSuccessModal({ open: false, element: null });
        }}
        onOk={() => {
          onSubmitFilter({
            filter: { codeResponsible: '', codeService: '', typePractice: '', orders: {} },
          });
          setDeleteSuccessModal({ open: false, element: null });
        }}
      />
    </Fragment>
  );
};

ResearchResponsible.displayName = 'Research Responsible Page component';

export default ResearchResponsible;
